/**
 * Google Tag Manager Event Tracking Utility
 * 
 * This file provides helper functions to push custom events to the dataLayer
 * for tracking in Google Analytics via GTM.
 */

/**
 * Push a custom event to the GTM dataLayer
 * @param {string} event - The event name
 * @param {Object} data - Additional event data
 */
export const pushToDataLayer = (event, data = {}) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event,
      ...data,
    });
    console.log('GTM Event:', event, data); // Remove in production if needed
  } else {
    console.warn('GTM dataLayer not available');
  }
};

// ============= Menu Events =============

/**
 * Track when a user views a menu category
 */
export const trackMenuCategoryView = (categoryName, categorySlug) => {
  pushToDataLayer('menu_category_view', {
    category_name: categoryName,
    category_slug: categorySlug,
  });
};

/**
 * Track when a user views a menu item
 */
export const trackMenuItemView = (itemName, itemSlug, categoryName, calories) => {
  pushToDataLayer('menu_item_view', {
    item_name: itemName,
    item_slug: itemSlug,
    category_name: categoryName,
    calories: calories || null,
  });
};

// ============= Location Events =============

/**
 * Track when a user views a location page
 */
export const trackLocationView = (locationName, locationSlug, region) => {
  pushToDataLayer('location_view', {
    location_name: locationName,
    location_slug: locationSlug,
    region: region,
  });
};

/**
 * Track when a user selects a location from the modal
 */
export const trackLocationSelect = (locationName, locationSlug) => {
  pushToDataLayer('location_select', {
    location_name: locationName,
    location_slug: locationSlug,
  });
};

// ============= CTA Events =============

/**
 * Track when a user clicks online ordering
 */
export const trackOnlineOrderClick = (locationName, source = 'unknown') => {
  pushToDataLayer('online_order_click', {
    location_name: locationName,
    click_source: source, // e.g., 'location_page', 'menu_item', 'header'
  });
};

/**
 * Track when a user clicks reservations/waitlist
 */
export const trackReservationClick = (locationName, source = 'unknown') => {
  pushToDataLayer('waitlist_click', {
    location_name: locationName,
    click_source: source,
  });
};

/**
 * Track when a user clicks to call a location
 */
export const trackPhoneClick = (locationName, phoneNumber, source = 'unknown') => {
  pushToDataLayer('phone_click', {
    location_name: locationName,
    phone_number: phoneNumber,
    click_source: source,
  });
};

/**
 * Track when a user clicks get directions
 */
export const trackDirectionsClick = (locationName, source = 'unknown') => {
  pushToDataLayer('directions_click', {
    location_name: locationName,
    click_source: source,
  });
};

// ============= Waitlist Events =============

/**
 * Track when a user opens the waitlist form
 */
export const trackWaitlistOpen = (locationName) => {
  pushToDataLayer('waitlist_open', {
    location_name: locationName,
  });
};

/**
 * Track when a user successfully joins the waitlist
 */
export const trackWaitlistSubmit = (locationName, partySize, estimatedWait) => {
  pushToDataLayer('waitlist_submit', {
    location_name: locationName,
    party_size: partySize,
    estimated_wait: estimatedWait,
  });
};

// ============= Contact Events =============

/**
 * Track when a user submits the contact form
 */
export const trackContactFormSubmit = (locationName = null) => {
  pushToDataLayer('contact_form_submit', {
    location_name: locationName,
  });
};

// ============= Navigation Events =============

/**
 * Track when a user uses the mega menu
 */
export const trackMegaMenuClick = (categoryName) => {
  pushToDataLayer('mega_menu_click', {
    category_name: categoryName,
  });
};

/**
 * Track when a user searches for locations
 */
export const trackLocationSearch = (searchQuery) => {
  pushToDataLayer('location_search', {
    search_query: searchQuery,
  });
};

// ============= Engagement Events =============

/**
 * Track when a user scrolls to a specific section
 */
export const trackSectionView = (sectionName) => {
  pushToDataLayer('section_view', {
    section_name: sectionName,
  });
};

/**
 * Track when a user views the loyalty program section
 */
export const trackLoyaltyView = () => {
  pushToDataLayer('loyalty_section_view', {});
};

/**
 * Track when a user clicks on a social media link
 */
export const trackSocialClick = (platform, url) => {
  pushToDataLayer('social_click', {
    platform: platform,
    url: url,
  });
};

// ============= E-commerce Style Events (for menu items) =============

/**
 * Track menu item "add to favorites" or similar future features
 */
export const trackMenuItemInteraction = (action, itemName, categoryName) => {
  pushToDataLayer('menu_item_interaction', {
    action: action, // e.g., 'favorite', 'share'
    item_name: itemName,
    category_name: categoryName,
  });
};

// ============= Page View Events =============

/**
 * Track virtual page views (for SPAs)
 * Note: You may want to track this automatically on route changes
 */
export const trackPageView = (pagePath, pageTitle) => {
  pushToDataLayer('virtual_page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

